import React, { useState, useCallback } from "react";
import { cn } from "../../lib/utils";
import { Logo } from "./Logo";
import { NavigationTabs } from "./navigation-tabs";
import { HeaderActions } from "./header-actions";
import { UserProfileDropdown } from "./user-profile-dropdown";
import { Bell, X, FileText, CheckCircle, LogOut } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";

const MobileMenuContent = React.memo(function MobileMenuContent({ isOpen, onClose, user, navigation }) {
  return (
    <>
      <div
        className={cn(
          "absolute inset-0 top-[65px] bg-black/50 z-40 lg:hidden transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />
      <div
        className={cn(
          "absolute top-[65px] left-0 right-0 bottom-0 w-full bg-neutral-1 z-50 lg:hidden flex flex-col shadow-2xl transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pb-20">
          {/* User Section */}
          <button
            onClick={() => {
              user.onProfileClick?.();
              onClose();
            }}
            className="w-full px-8 py-7 pt-12 hover:bg-neutral-2 transition-colors text-left"
          >
            <div className="flex items-center space-x-3">
              <Avatar className="w-10 h-10 p-2 flex justify-center items-center bg-secondary-6 border border-secondary-6 cursor-pointer">
                <AvatarFallback className="text-secondary-2 text-base font-normal ">
                  {user.initials}
                </AvatarFallback>
              </Avatar>
              <div className="text-base font-medium text-neutral-12">
                {user.name}
              </div>
            </div>
          </button>
          {/* Separator line */}
          <div className="mx-6 border-b border-neutral-3"></div>

          {/* Navigation Items */}
          <nav className="px-6 py-4">
            <div className="space-y-2">
              {navigation.tabs.map((tab) => {
                const isLoans =
                  String(tab.id || tab.label || "").toLowerCase() === "loans";
                const Icon = isLoans ? FileText : CheckCircle;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      navigation.onTabClick?.(tab);
                      onClose();
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-6 rounded-lg hover:bg-neutral-2 transition-colors text-left"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#FFF8F0]">
                      <Icon className="w-5 h-5 text-primary-2" />
                    </div>
                    <span className="text-base text-neutral-12">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Logout Section - fixed at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-neutral-1">
          {/* Separator line */}
          <div className="mx-6 border-t border-neutral-3"></div>
          <button
            onClick={() => {
              user.onLogout?.();
              onClose();
            }}
            className="w-full flex items-center space-x-3 px-12 py-6 hover:bg-neutral-2 transition-colors"
          >
            <div className="bg-label-red-1 p-2 rounded-md">
              <LogOut className="w-5 h-5 text-label-red-2 rounded-md" />
            </div>
            <span className="text-base text-neutral-12">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
})

export function ShellHeader({
  logo = { text: "MERCHANTS", url: null, alt: "Logo", onClick: null },
  navigation = {
    tabs: [
      {
        id: "loans",
        label: "Loans",
        path: "/borrower-portal/loans",
        active: true,
      },
      {
        id: "draw-request",
        label: "Draw Request",
        path: "/draw-request/loans",
        active: false,
      },
    ],
    onTabClick: null,
    activeRoute: "/borrower-portal/loans",
  },
  hasLeftNavigation = true,
  hasRightNavigation = true,
  actions = {
    showCheckRates: true,
    showApplyLoan: true,
    showLogout: false,
    onCheckRatesClick: null,
    onApplyLoanClick: null,
    onLogoutClick: null,
    disableApplyLoan: false,
  },
  user = {
    initials: "RH",
    image: null,
    name: "Rossie",
    onLogout: null,
    showProfile: true,
    onProfileClick: null,
  },
  onMenuClick = null,
  onNotificationClick = null,
  notificationCount = 0,
  className = "",
  hideHamburgerMenu = false
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleMenuClick = useCallback(() => {
    onMenuClick?.() ?? setIsMobileMenuOpen((prev) => !prev);
  }, [onMenuClick]);

  return (
    <>
      {/* Mobile Menu Content - always rendered for animation, visibility controlled by isOpen */}
      {!hideHamburgerMenu && (
        <MobileMenuContent
          isOpen={isMobileMenuOpen}
          onClose={() => {
            return setIsMobileMenuOpen(false);
          }}
          user={user}
          navigation={navigation}
        />
      )}

      {/* Sticky Header - always visible */}
      <header
        className={cn(
          "flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-12 py-4 border-b border-gray-200 bg-white sticky top-0 z-50 lg:relative lg:z-auto",
          className
        )}
      >
        {/* Mobile Menu Button - toggles between hamburger and X */}
        {!hideHamburgerMenu && (
          <button
            onClick={handleMenuClick}
            className=" lg:hidden
              p-[clamp(6px,2vw,10px)]
              hover:bg-gray-100
              rounded-md
              transition-all duration-200"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#3e4c6e]" />
            ) : (
              <div className="flex flex-col justify-center space-y-[clamp(4px,1vw,6px)]">
                <span className="h-0.5 w-[clamp(16px,5vw,24px)] bg-[#3e4c6e] rounded-full"></span>
                <span className="h-0.5 w-[clamp(16px,5vw,24px)] bg-[#3e4c6e] rounded-full"></span>
                <span className="h-0.5 w-[clamp(16px,5vw,24px)] bg-[#3e4c6e] rounded-full"></span>
              </div>
            )}
          </button>
        )}

        {/* Desktop Left Section */}
        <div className="hidden lg:flex items-center space-x-12">
          <Logo text={logo.text} logoUrl={logo.url} onClick={logo.onClick} />
          {hasLeftNavigation && (
            <NavigationTabs
              tabs={navigation.tabs}
              onTabClick={navigation.onTabClick}
              activeRoute={navigation.activeRoute}
            />
          )}
        </div>

        {/* Center Logo (Mobile & Tablet) */}
        <div
          className="lg:hidden
          absolute left-1/2 -translate-x-1/2
          scale-90 sm:scale-95 xs:scale-80
          transition-transform duration-200"
        >
          <Logo text={logo.text} logoUrl={logo.url} onClick={logo.onClick} />
        </div>

        {/* Right side actions */}
        {hasRightNavigation && (
          <div className='flex items-center space-x-3 sm:space-x-4 lg:space-x-6'>
            {/* Action buttons - hidden below lg breakpoint */}
            <div className='hidden lg:flex items-center space-x-4'>
              <HeaderActions actions={actions} />
            </div>

            {/* Notification Bell - visible on mobile only */}
            {user.showProfile && (
              <button
                onClick={onNotificationClick}
                className='relative
                    lg:hidden
                    p-[clamp(6px,2vw,10px)]
                    hover:bg-gray-100
                    rounded-md
                    transition-all duration-200
                    origin-center'
                aria-label='Notifications'
              >
                <Bell className='w-5 h-5 text-neutral-10' />
                {notificationCount > 0 && (
                  <span className='absolute top-0 right-0 grid place-items-center rounded-full bg-red-500 text-white font-medium min-w-[14px] h-[14px] px-1 text-[10px] sm:min-w-[16px] sm:h-[16px] sm:text-[11px]'>
                    {notificationCount > 99 ? "99+" : notificationCount}
                  </span>
                )}
              </button>
            )}

            {/* User Profile - hidden below lg breakpoint */}
            {user.showProfile && (
              <div className='hidden lg:block'>
                <UserProfileDropdown
                  userInitials={user.initials}
                  userImage={user.image}
                  userName={user.name}
                  onProfileClick={user.onProfileClick}
                  onLogoutClick={user.onLogout}
                />
              </div>
            )}
          </div>
        )}
      </header>
    </>
  );
}
